import { useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { trpc } from "@/trpc/client";
import {
  consumeXIntegrationOAuthState,
  xIntegrationRedirectUri,
} from "@/lib/x-integration-oauth";

/**
 * X's redirect back after the consent screen (#680) — the twin of
 * `integrations_.linkedin-callback.tsx`, and the second half of a connect whose
 * first half is `ConnectXDialog` (the author's own app credentials, stored
 * server-side before the redirect).
 *
 * The URL carries no `workspaceId`: X matches `redirect_uri` exactly against
 * the app's registered callback URLs, so there is one callback URL for every
 * workspace — and here the author registered it in their own app by hand, so it
 * has to stay exactly this. The workspace, and PKCE's `code_verifier`, are
 * recovered from the OAuth `state` instead (`lib/x-integration-oauth.ts`).
 *
 * It still lives under `/dashboard`, so the authenticated-subtree guard in
 * `dashboard.tsx` applies; the `_` suffix keeps it out of the integrations
 * page's subtree while serving that URL prefix.
 */
export const Route = createFileRoute("/dashboard/integrations_/x-callback")({
  component: XIntegrationCallbackPage,
});

function XIntegrationCallbackPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const processedRef = useRef(false);

  // The workspace is resolved from the OAuth `state` inside the effect, so the
  // mutation's callbacks read it back off `variables` rather than closing over
  // a route param that this URL no longer carries.
  const connectMutation = useMutation({
    ...trpc.authenticatedRouterFunction.connectXIntegration.mutationOptions(),
    onSuccess: (result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: trpc.authenticatedRouterFunction.getIntegrations.queryOptions({
          workspaceId: variables.workspaceId,
        }).queryKey,
      });
      toast.success("X connected", { description: result.account });
    },
    onError: (err) => {
      toast.error("Could not connect X", {
        description: err.message ?? "Unknown error occurred.",
      });
    },
    onSettled: (_data, _err, variables) => {
      void navigate({
        to: "/dashboard/w/$workspaceId/integrations",
        params: { workspaceId: variables.workspaceId },
      });
    },
  });

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    // Consumed first, and unconditionally: the state is single-use whatever the
    // outcome, and its workspace is also where an error should land the author
    // back.
    const pending = consumeXIntegrationOAuthState(params.get("state"));

    const goBack = () => {
      void (pending
        ? navigate({
            to: "/dashboard/w/$workspaceId/integrations",
            params: { workspaceId: pending.workspaceId },
          })
        : // No state means no workspace to return to; `/dashboard` resolves the
          // user's first workspace.
          navigate({ to: "/dashboard" }));
    };

    const error = params.get("error");
    if (error) {
      toast.error(
        params.get("error_description") ||
          "X connection did not complete. Please try again.",
      );
      goBack();
      return;
    }

    const code = params.get("code");
    if (!code || !pending) {
      toast.error("Invalid X callback. Please try again.");
      goBack();
      return;
    }

    connectMutation.mutate({
      workspaceId: pending.workspaceId,
      code,
      codeVerifier: pending.codeVerifier,
      redirectUri: xIntegrationRedirectUri(),
    });
    // One-shot on mount: `consumeXIntegrationOAuthState` deletes the single-use
    // state, so a second run could only ever fail validation. `processedRef` is
    // the real guard — naming the deps would just re-fire on `connectMutation`'s
    // per-render identity and be short-circuited by it. Same shape as the
    // LinkedIn callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="text-sm text-muted-foreground">Connecting X...</div>
    </div>
  );
}
